<template>
  <div>
    <b-navbar toggleable="md" type="dark" variant="info" class="pt-0 pb-0">
      <div class="container">
        <b-navbar-toggle target="nav_collapse_permisos"></b-navbar-toggle>
        <h1 class="text-center navbar-brand pt-0 pb-0 mb-0">Gestión de Permisos</h1>
        <b-collapse is-nav id="nav_collapse_permisos">
          <b-navbar-nav class="ml-auto">
            <b-button v-if="esSuperusuario" @click="abrirModalCrear" variant="success" size="sm" class="ml-2">
              <b-icon icon="plus-circle"></b-icon> Crear Permiso
            </b-button>
          </b-navbar-nav>
        </b-collapse>
      </div>
    </b-navbar>

    <div class="container permisos_wrapper mt-3">
      <!-- Selector de Grupo -->
      <div class="row mb-3">
        <div class="col-md-6">
          <b-form-group label="Filtrar por Grupo:" label-for="grupo-select">
            <b-form-select 
              id="grupo-select" 
              v-model="grupoSeleccionado"
              :options="gruposOptions"
              @change="filtrarPorGrupo"
            >
              <template #first>
                <b-form-select-option :value="null">-- Todos los permisos --</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </div>
        <div class="col-md-6 d-flex align-items-end">
          <b-button v-if="grupoSeleccionado && esSuperusuario" @click="guardarPermisosGrupo" variant="success" class="mb-3">
            Guardar Permisos del Grupo
          </b-button>
        </div>
      </div>

      <!-- Buscador -->
      <div class="row mb-3">
        <div class="col-md-12">
          <b-form-input
            v-model="filtroTexto"
            placeholder="Buscar por código o recurso..."
            debounce="300"
          />
        </div>
      </div>

      <!-- Lista de Permisos -->
      <div v-if="!grupoSeleccionado" class="alert alert-info">
        <b-icon icon="info-circle"></b-icon> Seleccione un grupo para asignar permisos
      </div>

      <div class="permisos-list">
        <!-- Iterar por cada grupo de permisos -->
        <div 
          v-for="(permisos, grupo) in permisosAgrupados" 
          :key="grupo"
          class="grupo-permisos mb-4"
        >
          <h4 class="grupo-titulo mb-3">
            <b-icon icon="folder" variant="primary"></b-icon>
            {{ grupo }}
          </h4>
          
          <!-- Grid de permisos -->
          <div class="row">
            <div 
              v-for="permiso in permisos" 
              :key="permiso.recurso"
              class="col-md-4 col-lg-3 mb-3"
            >
              <div 
                class="permiso-card p-2 border rounded h-100"
                :class="{'permiso-editable': !grupoSeleccionado}"
                @click="!grupoSeleccionado && abrirModalEditar(permiso)"
              >
                <div class="d-flex align-items-start">
                  <b-form-checkbox 
                    v-if="grupoSeleccionado && esSuperusuario"
                    v-model="permisosAsignados[permiso.recurso]"
                    :value="true"
                    :unchecked-value="false"
                    class="mr-2 mt-1"
                  />
                  <b-icon 
                    v-else 
                    icon="pencil-square" 
                    variant="primary" 
                    class="mr-2 mt-1" 
                    title="Editar permiso"
                  ></b-icon>
                  
                  <div class="flex-grow-1">
                    <div class="permiso-codigo font-weight-bold mb-1">
                      <code>{{ permiso.descripcion ?? permiso.recurso }}</code>
                    </div>
                    <div class="permiso-recurso text-muted">
                      {{ permiso.recurso }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="Object.keys(permisosAgrupados).length === 0" class="alert alert-warning">
          No se encontraron permisos que coincidan con la búsqueda.
        </div>
      </div>

      <!-- Botón de guardar al final -->
      <div v-if="grupoSeleccionado" class="row mt-4">
        <div class="col-md-12 text-center">
          <b-button v-if="esSuperusuario" @click="guardarPermisosGrupo" variant="success" size="lg">
            Guardar Permisos del Grupo
          </b-button>
        </div>
      </div>
    </div>

    <!-- Modal Crear Permiso -->
    <b-modal 
      v-model="mostrarModalCrear" 
      title="Crear Nuevo Permiso"
      @ok="crearPermiso"
      @cancel="cerrarModalCrear"
      ok-title="Guardar"
      cancel-title="Cancelar"
      no-close-on-backdrop
    >
      <b-form>
        <b-form-group label="Recurso:" label-for="input-recurso">
          <b-form-input
            id="input-recurso"
            v-model="nuevoPermiso.recurso"
            placeholder="Ejemplo: estudiantes/lista"
            required
          ></b-form-input>
          <small class="text-muted">Formato: categoria/accion</small>
        </b-form-group>

        <b-form-group label="Grupo:" label-for="input-grupo">
          <b-form-input
            id="input-grupo"
            v-model="nuevoPermiso.grupo"
            placeholder="Ejemplo: Administradores"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Descripción:" label-for="input-descripcion">
          <b-form-textarea
            id="input-descripcion"
            v-model="nuevoPermiso.descripcion"
            placeholder="Descripción del permiso (opcional)"
            rows="3"
          ></b-form-textarea>
        </b-form-group>
      </b-form>
    </b-modal>

    <!-- Modal Editar Permiso -->
    <b-modal 
      v-model="mostrarModalEditar" 
      title="Editar Permiso"
      @ok="editarPermiso"
      @cancel="cerrarModalEditar"
      ok-title="Guardar"
      cancel-title="Cancelar"
      no-close-on-backdrop
    >
      <b-form>
        <b-form-group label="Código:" label-for="edit-codigo">
          <b-form-input
            id="edit-codigo"
            v-model="permisoEditar.codigo"
            readonly
            disabled
          ></b-form-input>
          <small class="text-muted">Solo lectura</small>
        </b-form-group>

        <b-form-group label="Recurso:" label-for="edit-recurso">
          <b-form-input
            id="edit-recurso"
            v-model="permisoEditar.recurso"
            placeholder="Ejemplo: estudiantes/lista"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Grupo:" label-for="edit-grupo">
          <b-form-input
            id="edit-grupo"
            v-model="permisoEditar.grupo"
            placeholder="Ejemplo: Administradores"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Descripción:" label-for="edit-descripcion">
          <b-form-textarea
            id="edit-descripcion"
            v-model="permisoEditar.descripcion"
            placeholder="Descripción del permiso (opcional)"
            rows="3"
          ></b-form-textarea>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
  import {mapState, mapActions, mapMutations, mapGetters} from 'vuex';

  export default {
    name: 'PermisosLista',
    data () {
      return {
        grupoSeleccionado: null,
        permisosAsignados: {},
        filtroTexto: '',
        mostrarModalCrear: false,
        mostrarModalEditar: false,
        nuevoPermiso: {
          recurso: '',
          grupo: '',
          descripcion: ''
        },
        permisoEditar: {
          id: null,
          codigo: '',
          recurso: '',
          grupo: '',
          descripcion: ''
        }
      }
    },
    mounted () {
      // Cargar la lista de permisos
      if(!this.lista || (this.lista && this.lista.length == 0)){
        this.fetchLista();
      }
      
      // Cargar la lista de grupos
      if(!this.listaGrupos || (this.listaGrupos && this.listaGrupos.length == 0)){
        this.fetchGrupos();
      }
    },
    methods:{
      ...mapActions({
        fetchLista: 'permisos/fetchLista',
        fetchGrupos: 'grupos/fetchLista',
        fetchPermisosGrupo: 'permisos/fetchPermisosGrupo',
        actualizarPermisosGrupo: 'permisos/actualizarPermisosGrupo',
        crearPermisoStore: 'permisos/crear',
        actualizarPermisoStore: 'permisos/actualizar',
      }),
      async filtrarPorGrupo() {
        if(this.grupoSeleccionado) {
          try {
            // Consultar los permisos disponibles del grupo usando la acción del store
            const response = await this.fetchPermisosGrupo({
              grupo_id: this.grupoSeleccionado
            });
            
            // Inicializar todos los permisos como no asignados
            this.permisosAsignados = {};
            
            // La respuesta es un objeto con { grupo: {...}, permisos: [{id, recurso, codigo}] }
            const permisosGrupo = response.permisos || [];
            
            // Marcar los permisos que tiene el grupo (buscar por recurso)
            this.lista.forEach(permiso => {
              // Verificar si el permiso está en el array de permisos del grupo
              const tienePermiso = permisosGrupo.some(p => p.recurso === permiso.recurso);
              this.permisosAsignados[permiso.recurso] = tienePermiso;
            });
            
          } catch (error) {
            this.$bvToast.toast('Error al cargar los permisos del grupo', {
              title: 'Error',
              variant: 'danger',
              autoHideDelay: 3000
            });
            
            // Inicializar todo en false en caso de error
            this.permisosAsignados = {};
            this.lista.forEach(permiso => {
              this.permisosAsignados[permiso.recurso] = false;
            });
          }
        } else {
          this.permisosAsignados = {};
        }
      },
      async guardarPermisosGrupo() {
        if(!this.grupoSeleccionado) {
          return;
        }
        
        try {
          // Obtener los recursos de los permisos asignados
          const recursosSeleccionados = Object.keys(this.permisosAsignados)
            .filter(recurso => this.permisosAsignados[recurso]);
          
          // Obtener los IDs de los permisos seleccionados
          const permisosIds = this.lista
            .filter(permiso => recursosSeleccionados.includes(permiso.recurso))
            .map(permiso => permiso.id);
          
          // Llamar a la acción del store que actualiza los permisos del grupo
          const response = await this.actualizarPermisosGrupo({
            grupo_id: this.grupoSeleccionado,
            permisos_ids: permisosIds
          });
          
          // Mostrar mensaje con detalles de los cambios
          const { mensaje, cambios } = response;
          const detalles = `${cambios.agregados} agregados, ${cambios.eliminados} eliminados`;
          
          this.$bvToast.toast(detalles, {
            title: mensaje || 'Permisos actualizados',
            variant: 'success',
            autoHideDelay: 4000
          });
          
          // Recargar grupos para tener datos actualizados
          await this.fetchGrupos();
          
        } catch (error) {
          this.$bvToast.toast('Error al guardar los permisos del grupo', {
            title: 'Error',
            variant: 'danger',
            autoHideDelay: 5000
          });
        }
      },
      abrirModalCrear() {
        this.nuevoPermiso = {
          recurso: '',
          grupo: '',
          descripcion: ''
        };
        this.mostrarModalCrear = true;
      },
      cerrarModalCrear() {
        this.mostrarModalCrear = false;
        this.nuevoPermiso = {
          recurso: '',
          grupo: '',
          descripcion: ''
        };
      },
      async crearPermiso(evt) {
        evt.preventDefault();
        
        // Validar campos requeridos
        if (!this.nuevoPermiso.recurso || !this.nuevoPermiso.grupo) {
          this.$bvToast.toast('Debe completar el recurso y el grupo', {
            title: 'Error de validación',
            variant: 'warning',
            autoHideDelay: 3000
          });
          return;
        }
        
        try {
          await this.crearPermisoStore(this.nuevoPermiso);
          
          this.$bvToast.toast('Permiso creado exitosamente', {
            title: 'Éxito',
            variant: 'success',
            autoHideDelay: 3000
          });
          
          // Recargar la lista de permisos
          await this.fetchLista();
          
          // Cerrar el modal
          this.mostrarModalCrear = false;
          this.cerrarModalCrear();
          
        } catch (error) {
          this.$bvToast.toast('Error al crear el permiso', {
            title: 'Error',
            variant: 'danger',
            autoHideDelay: 5000
          });
        }
      },
      abrirModalEditar(permiso) {
        if (!this.permiso('F803E6E4')) {
          this.$bvToast.toast('No tienes permisos para realizar esta acción!', {
            title: 'Error',
            variant: 'danger',
            autoHideDelay: 5000
          });
          return;
        }
        this.permisoEditar = {
          id: permiso.id,
          codigo: permiso.codigo,
          recurso: permiso.recurso,
          grupo: permiso.grupo || '',
          descripcion: permiso.descripcion || ''
        };
        this.mostrarModalEditar = true;
      },
      cerrarModalEditar() {
        this.mostrarModalEditar = false;
        this.permisoEditar = {
          id: null,
          codigo: '',
          recurso: '',
          grupo: '',
          descripcion: ''
        };
      },
      async editarPermiso(evt) {
        evt.preventDefault();
        
        // Validar campos requeridos
        if (!this.permisoEditar.recurso || !this.permisoEditar.grupo) {
          this.$bvToast.toast('Debe completar el recurso y el grupo', {
            title: 'Error de validación',
            variant: 'warning',
            autoHideDelay: 3000
          });
          return;
        }
        
        try {
          await this.actualizarPermisoStore({
            id: this.permisoEditar.id,
            data: {
              recurso: this.permisoEditar.recurso,
              grupo: this.permisoEditar.grupo,
              descripcion: this.permisoEditar.descripcion
            }
          });
          
          this.$bvToast.toast('Permiso actualizado exitosamente', {
            title: 'Éxito',
            variant: 'success',
            autoHideDelay: 3000
          });
          
          // Recargar la lista de permisos
          await this.fetchLista();
          
          // Cerrar el modal
          this.mostrarModalEditar = false;
          this.cerrarModalEditar();
          
        } catch (error) {
          this.$bvToast.toast('Error al actualizar el permiso', {
            title: 'Error',
            variant: 'danger',
            autoHideDelay: 5000
          });
        }
      }
    },
    computed: {
      ...mapState({
        lista: state => state.permisos.lista,
        listaGrupos: state => state.grupos.lista,
        user: state => state.auth.user,
      }),
      ...mapGetters({
        permiso: 'auth/permiso'
      }),
      esSuperusuario() {
        return this.user && this.user.data && this.user.data.grupo_id == 2;
      },
      listaFiltrada() {
        if(!this.filtroTexto) {
          return this.lista;
        }
        
        const textoBusqueda = this.filtroTexto.toLowerCase();
        return this.lista.filter(permiso => {
          const codigo = permiso.codigo ? permiso.codigo.toLowerCase() : '';
          const recurso = permiso.recurso ? permiso.recurso.toLowerCase() : '';
          
          return codigo.includes(textoBusqueda) || recurso.includes(textoBusqueda);
        });
      },
      permisosAgrupados() {
        // Agrupar permisos por el campo grupo
        const grupos = {};
        
        this.listaFiltrada.forEach(permiso => {
          // Usar el campo grupo del permiso
          const nombreGrupo = permiso.grupo || 'Sin grupo';
          
          if (!grupos[nombreGrupo]) {
            grupos[nombreGrupo] = [];
          }
          
          grupos[nombreGrupo].push(permiso);
        });
        
        // Ordenar los permisos dentro de cada grupo por código
        Object.keys(grupos).forEach(grupo => {
          grupos[grupo].sort((a, b) => {
            const codigoA = a.codigo || '';
            const codigoB = b.codigo || '';
            return codigoA.localeCompare(codigoB);
          });
        });
        
        return grupos;
      },
      gruposOptions() {
        if(!this.listaGrupos) return [];
        return this.listaGrupos.map(grupo => ({
          value: grupo.id,
          text: grupo.nombre
        }));
      }
    }
  }
</script>

<style scoped>
  .permisos_wrapper {
    margin-bottom: 50px;
  }
  
  .grupo-permisos {
    background-color: #ffffff;
    padding: 15px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
  }
  
  .grupo-titulo {
    color: #495057;
    font-size: 1.2rem;
    border-bottom: 2px solid #17a2b8;
    padding-bottom: 8px;
    margin-bottom: 15px;
  }
  
  .permiso-card {
    background-color: #f8f9fa;
    transition: all 0.2s ease;
  }
  
  .permiso-card.permiso-editable {
    cursor: pointer;
  }
  
  .permiso-card.permiso-editable:hover {
    background-color: #e9ecef;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transform: translateY(-2px);
  }
  
  .permiso-codigo code {
    font-family: inherit;
    background-color: #e3f2fd;
    color: #1976d2;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 600;
    word-break: break-word;
    overflow-wrap: break-word;
    display: inline-block;
    max-width: 100%;
    white-space: normal;
  }
  
  .permiso-recurso {
    font-size: 0.75rem;
    color: #6c757d;
    word-break: break-word;
    overflow-wrap: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
    max-height: 4.2rem;
  }
</style>
